import { useCallback, useState, useEffect } from "react";
import "../styles/nimbly2.css";
import { useProject } from '../contexts/ProjectContext';
import { useEnvironmentConfigContext } from '../contexts/EnvironmentConfigContext';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { Button } from "@progress/kendo-react-buttons";
import { xIcon } from "@progress/kendo-svg-icons";
import loadinggif from "../assets/loading.gif";

const ExpandedStreetviewPole = ({ item, closePopup }) => {
    const { selectedProject, GCP_Project, Dataset_ID } = useProject();
    const { environmentConfig } = useEnvironmentConfigContext();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [poleData, setPoleData] = useState(null);

    const handleTabSelect = (e) => {
        setActiveTab(e.selected);
    };

    useEffect(() => {
        const controller = new AbortController();

        const populatePolePopupData = () => {
            if (!item) {
                return;
            }
            setLoading(true);

            fetch(`${process.env.REACT_APP_WEB_API_URL}/api-common/poles/PoleLookup`, {
                method: 'POST',
                headers: {
                    'frontendenvironmentconfig': environmentConfig,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    Dataset_ID: Dataset_ID,
                    GCP_Project: GCP_Project,
                    Project: selectedProject,
                    GlobalID: item.info.GlobalID
                }),
                signal: controller.signal,
            })
            .then((res) => res.json())
            .then((data) => {
                setPoleData(data);
                setLoading(false);
            })
            .catch((error) => {
                if (error.name === "AbortError") {
                    return;
                }
                setLoading(false);
            });
        }

        if (selectedProject && GCP_Project && Dataset_ID) {
            populatePolePopupData();
        }

        return () => {
            controller.abort();
        };
    }, [item, selectedProject, GCP_Project, Dataset_ID, environmentConfig]);

    if (!item) {
        return (
            <div className="pole-details-container-loading">
                <div className="no-data-text">No Selected Pole</div>
            </div>
        ); 
    }

    if (loading) {
        return (
            <div className="pole-details-container-loading">
                <img className="nimbly2-map-loader-gif" src={loadinggif} alt="Loading..." />
            </div>
        );
    }

    const polesDict = poleData?.fieldDict?.Poles || {};
    
    const extraTables = Object.keys(poleData?.fieldDict || {}).filter(
        key => key !== 'Poles' && key !== 'Location_Details'
    );
    
    return (
        <div className="pole-details-container">

            <div className="pole-photo-hero">
                <Button rounded={"full"} fillMode={"solid"} svgIcon={xIcon} themeColor={"secondary"} className='pole-details-container-close-button' onClick={closePopup} />
                {poleData?.photosDict && poleData.photosDict["Photo 1"] ? (
                    <a href={poleData.photosDict["Photo 1"]["Photo Path"]} target="_blank" rel="noreferrer">
                        <img src={poleData.photosDict["Photo 1"]["Photo Path"]} alt={poleData.photosDict["Photo 1"]['Photo Name'] || 'Pole location'} title={poleData.photosDict["Photo 1"]["Photo Name"]} />
                    </a>
                ) : (
                    <div className="no-photo-placeholder">No Photo Available</div>
                )}
            </div>

            <TabStrip selected={activeTab} onSelect={handleTabSelect}>
                <TabStripTab title="Overview">
                    <div className="dynamic-info-list">
                        {!poleData?.fieldDict?.Poles ? (
                            <div className="no-data-text">No Pole Data</div>
                        ) : (
                            <>
                                {Object.entries(polesDict).map(([key, value]) => (
                                    <div className="info-flex-row" key={key}>
                                        <div className="info-label">{key}</div>
                                        <div className="info-value">{value ?? ''}</div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </TabStripTab>
                
                <TabStripTab title="Location">
                    <div className="dynamic-info-list">
                        {!poleData?.fieldDict?.Location_Details ? (
                            <div className="no-data-text">No Location Data</div>
                        ) : (
                            Object.entries(poleData.fieldDict.Location_Details).map(([key, value]) => (
                                <div className="info-flex-row" key={key}>
                                    <div className="info-label">{key}</div>
                                    <div className="info-value">{value ?? ''}</div>
                                </div>
                            ))
                        )}
                    </div>
                </TabStripTab>

                {extraTables.map(tableName => (
                    <TabStripTab title={tableName} key={tableName}>
                        <div className="dynamic-info-list">
                            {Object.entries(poleData.fieldDict[tableName]).map(([key, value]) => (
                                <div className="info-flex-row" key={key}>
                                    <div className="info-label">{key}</div>
                                    <div className="info-value">{value ?? ''}</div>
                                </div>
                            ))}
                        </div>
                    </TabStripTab>
                ))}
                
                <TabStripTab title="Photos">
                    {Object.keys(poleData?.photosDict || {}).length === 0 ? (
                        <div className="no-data-text">No Photo Data</div>
                    ) : (
                        Object.keys(poleData.photosDict).map((photo) => (
                            <div key={photo} className="dynamic-info-list">
                                { photo !== "Photo 1" && <div className="info-section-divider"></div> } 
                                <div className="info-flex-centered-row">
                                    <a href={poleData.photosDict[photo]["Photo Path"]} target="_blank" rel="noreferrer">
                                        <img src={poleData.photosDict[photo]["Photo Path"]} alt={photo} title={poleData.photosDict[photo]["Photo Name"]} />
                                    </a>
                                </div>
                                <div className="info-flex-row">
                                    <div className="info-label">Global ID</div>
                                    <div className="info-value">{poleData.photosDict[photo]["GlobalID"] ?? ''}</div>
                                </div>
                                <div className="info-flex-row">
                                    <div className="info-label">Photo Name</div>
                                    <div className="info-value">{poleData.photosDict[photo]["Photo Name"] ?? ''}</div>
                                </div>
                                <div className="info-flex-row">
                                    <div className="info-label">Rel GlobalID</div>
                                    <div className="info-value">{poleData.photosDict[photo]["Rel GlobalID"] ?? ''}</div>
                                </div>
                            </div>
                        ))
                    )}
                </TabStripTab>
                
                <TabStripTab title="Attacher">
                    {Object.keys(poleData?.attachmentsDict || {}).length === 0 ? (
                        <div className="no-data-text">No Attachment Data</div>
                    ) : (
                        Object.entries(poleData.attachmentsDict).map(([key, value]) => {
                            const sectionTitle = key;
                            const sectionRows = Object.entries(value).map(([currentKey, currentValue]) => (
                                <div className="info-flex-row" key={currentKey}>
                                    <div className="info-label">{currentKey}</div>
                                    <div className="info-value">{currentValue ?? ''}</div>
                                </div>
                            ));
                            return (
                                <div key={sectionTitle} className="dynamic-info-list">
                                    { sectionTitle !== "Attacher 1" && <div className="info-section-divider"></div> } 
                                    <div className="info-section-title">{sectionTitle}</div>
                                    {sectionRows}
                                </div>
                            );
                        })
                    )}
                </TabStripTab>
            </TabStrip>
        </div>
    );
};

export default ExpandedStreetviewPole;